/*!
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog, MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { MatLegacySelectChange } from '@angular/material/legacy-select';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import {
  EFormSubmitResult,
  EMessageType,
  ESmsOptInStatus,
  IDriverDetails,
  IDriverEmailMessage,
  IDriverSmsMessage,
  IMapContextMenuPermissions,
  ISmsInvitation,
  ISmsOptInStatus
} from '../../../core/models';
import { SendEmailToDriver, SendSmsInvitationToDriver, SendSmsToDriver } from '../../../core/store/actions';
import {
  getDriverDetails,
  getIsEmailSent,
  getIsSmsSent,
  getMapContextMenuPermissions,
  getSmsInvitation,
  getSmsOptInStatus
} from '../../../core/store/selectors';

@Component({
  selector: 'fm-driver-message',
  templateUrl: './driver-message.component.html',
  styleUrls: ['./driver-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DriverMessageComponent implements OnInit, OnDestroy {
  public userPermissions$: Observable<IMapContextMenuPermissions>;
  public smsOptInStatus$: Observable<ISmsOptInStatus>;
  public smsInvitation$: Observable<ISmsInvitation>;
  public driverDetails$: Observable<IDriverDetails>;
  public isEmailSent$: Observable<boolean>;
  public isSmsSent$: Observable<boolean>;
  public driverId: number;
  public phoneNumber: string;
  public EMessageType = EMessageType;
  public isSendingFailed = false;
  public isSendingSucceeded = false;
  public messageForm: UntypedFormGroup;
  public isFormSubmitDisabled: boolean;
  public isFormSubmitted: boolean;
  public isFormSubmitting: boolean;
  public formSubmittingResult: string;
  public selectedMessageType: EMessageType = EMessageType.Email;

  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    @Inject(MAT_LEGACY_DIALOG_DATA) private readonly _dialogData: { driverId: number },
    private readonly _dialog: MatLegacyDialog,
    private readonly _store: Store,
    private readonly _changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.userPermissions$ = this._store.pipe(select(getMapContextMenuPermissions));
    this.driverDetails$ = this._store.pipe(select(getDriverDetails));
    this.isEmailSent$ = this._store.pipe(select(getIsEmailSent));
    this.smsOptInStatus$ = this._store.pipe(select(getSmsOptInStatus));
    this.smsInvitation$ = this._store.pipe(select(getSmsInvitation));
    this.isSmsSent$ = this._store.pipe(select(getIsSmsSent));
    this.driverId = this._dialogData.driverId;

    this.driverDetails$
      .pipe(
        takeUntil(this._unsubscribe$),
        filter(driver => !!driver)
      )
      .subscribe(driver => {
        this._setMessageData(driver);
        this._changeDetectorRef.markForCheck();
      });

    this.isEmailSent$
      .pipe(
        takeUntil(this._unsubscribe$),
        filter(isSent => isSent !== null && isSent !== undefined)
      )
      .subscribe(isSent => {
        this._onMessageSent(isSent);
        this._changeDetectorRef.markForCheck();
      });

    this.isSmsSent$
      .pipe(
        takeUntil(this._unsubscribe$),
        filter(isSent => isSent !== null && isSent !== undefined)
      )
      .subscribe(isSent => {
        this._onMessageSent(isSent);
        this._changeDetectorRef.markForCheck();
      });

    this.messageForm = this._formBuilder.group({
      messageType: this._formBuilder.control(EMessageType.Email),
      emailMessage: this._formBuilder.group({
        email: [null, Validators.compose([Validators.required, Validators.email])],
        message: [null, Validators.required]
      }),
      smsMessage: this._formBuilder.group({
        message: [null, Validators.required]
      })
    });

    this.messageForm.valueChanges.pipe(takeUntil(this._unsubscribe$)).subscribe(() => {
      this.messageForm.markAllAsTouched();
      this._changeDetectorRef.markForCheck();
    });

    combineLatest([this.userPermissions$, this.smsOptInStatus$])
      .pipe(
        takeUntil(this._unsubscribe$),
        map(
          ([permissions, smsOptInStatus]: [IMapContextMenuPermissions, ISmsOptInStatus]) =>
            !permissions.textMessaging || !this.phoneNumber || !smsOptInStatus
        )
      )
      .subscribe(disableMessageType => {
        if (disableMessageType) {
          this.messageForm.get('messageType').disable();
        } else {
          this.messageForm.get('messageType').enable();
        }
      });
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onSelectionChanged(event: MatLegacySelectChange): void {
    this.selectedMessageType = event.value;
    this.smsOptInStatus$.pipe(take(1)).subscribe(smsOptInStatus => {
      this.isFormSubmitted = false;
      this.isFormSubmitDisabled =
        this.selectedMessageType === EMessageType.Sms && smsOptInStatus.OptInStatus !== ESmsOptInStatus.CanSendSms;
    });
  }

  public onSendMessageClick(): void {
    this.isFormSubmitted = true;
    if (this.selectedMessageType === EMessageType.Email && this.messageForm.get('emailMessage').valid) {
      this.isFormSubmitting = true;
      this._sendEmail();
    } else if (this.selectedMessageType === EMessageType.Sms && this.messageForm.get('smsMessage').valid) {
      this.isFormSubmitting = true;
      this._sendSms();
    }
  }

  public onSendSmsInvitation(): void {
    this._store.dispatch(new SendSmsInvitationToDriver(this.driverId));
  }

  private _sendEmail(): void {
    const message: IDriverEmailMessage = {
      Recipient: this.messageForm.value.emailMessage.email,
      Message: this.messageForm.value.emailMessage.message
    };
    this._store.dispatch(new SendEmailToDriver(message));
  }

  private _sendSms(): void {
    const message: IDriverSmsMessage = {
      DriverId: this.driverId,
      Message: this.messageForm.value.smsMessage.message
    };
    this._store.dispatch(new SendSmsToDriver(message));
  }

  private _onMessageSent(isSent: boolean): void {
    this.isFormSubmitting = false;
    if (isSent) {
      this.formSubmittingResult = EFormSubmitResult.Success;
      setTimeout(() => this._dialog.closeAll(), 2000);
    } else {
      this.formSubmittingResult = EFormSubmitResult.Error;
    }
  }

  private _setMessageData(driver: IDriverDetails): void {
    this.messageForm.get('emailMessage').get('email').setValue(driver.Email);
    if (driver.PhoneCountry && driver.PhoneNumber) {
      this.phoneNumber = driver.PhoneNumber;
    }
  }
}
